import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FaArrowLeft } from 'react-icons/fa/index.js';
import callOysterApi from '../../utils/call-oyster-api.js';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import '../../styles/TeamTasksPage.css';

const getAvailableQuarters = () =>
    Array.from({ length: 8 }).map((_, i) => {
        const d = new Date();
        d.setMonth(d.getMonth() - 3 * i);
        const y = d.getFullYear();
        const q = Math.floor(d.getMonth() / 3) + 1;
        return `${y}-Q${q}`;
    });

const normalizeUser = (user, teamName) => ({
    email: (user?.email || '').toLowerCase(),
    name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
    team: teamName || 'Unassigned'
});

/** One row per email for report; prefer primary hdTeam over mirrored Leadership roster row */
const dedupeReportUsersByEmail = (rows) => {
    const lists = rows.reduce((acc, row) => {
        const list = acc.get(row.email) || [];
        list.push(row);
        acc.set(row.email, list);
        return acc;
    }, new Map());

    return Array.from(lists.values()).map((list) => {
        const nonLeadership = list.find(
            (r) => (r.team || '').toLowerCase() !== 'leadership'
        );
        return nonLeadership || list[0];
    });
};

const getCompletionRate = (completed, total) => {
    if (!total) return 0;
    return Math.round((completed / total) * 100);
};

const escapeCsvValue = (value) => {
    const text = String(value ?? '');
    if (text.includes('"') || text.includes(',') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
};

const parseBooleanParam = (value, fallback = false) => {
    if (value === '1' || value === 'true') return true;
    if (value === '0' || value === 'false') return false;
    return fallback;
};

const TaskReportPage = () => {
    const { user } = useCurrentUser();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [allTasks, setAllTasks] = useState([]);
    const [teamUsers, setTeamUsers] = useState([]);
    const [reportTasksForbidden, setReportTasksForbidden] = useState(false);
    const [searchTerm, setSearchTerm] = useState(
        searchParams.get('search') || ''
    );
    const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'total');
    const [sortDirection, setSortDirection] = useState(
        searchParams.get('sortDirection') || 'desc'
    );
    const [hideZeroTasks, setHideZeroTasks] = useState(
        parseBooleanParam(searchParams.get('hideZero'), false)
    );

    const availableQuarters = useMemo(() => getAvailableQuarters(), []);
    const initialQuarter = searchParams.get('quarter') || availableQuarters[0];
    const [selectedQuarter, setSelectedQuarterState] = useState(initialQuarter);

    const isLeadership = user?.isItLeadership;
    const isTeamLead = user?.isTeamLead;

    const setSelectedQuarter = (quarter) => {
        setSelectedQuarterState(quarter);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('quarter', quarter);
                return next;
            },
            { replace: true }
        );
    };

    useEffect(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('quarter', selectedQuarter);

                if (searchTerm.trim()) {
                    next.set('search', searchTerm.trim());
                } else {
                    next.delete('search');
                }

                next.set('sortBy', sortBy);
                next.set('sortDirection', sortDirection);
                next.set('hideZero', hideZeroTasks ? '1' : '0');

                return next;
            },
            { replace: true }
        );
    }, [
        hideZeroTasks,
        searchTerm,
        selectedQuarter,
        setSearchParams,
        sortBy,
        sortDirection
    ]);

    useEffect(() => {
        if (!selectedQuarter || !availableQuarters.includes(selectedQuarter)) {
            setSelectedQuarter(availableQuarters[0]);
        }
    }, [availableQuarters, selectedQuarter]);

    useEffect(() => {
        if (!user || !selectedQuarter) return;

        let cancelled = false;
        const fetchReportData = async () => {
            setIsLoading(true);
            try {
                const [usersRes, tasksRes] = await Promise.all([
                    callOysterApi('/dashboard/team-users', 'GET'),
                    callOysterApi(
                        `/dashboard/tasks?quarter=${selectedQuarter}&page=1&pageSize=1000`,
                        'GET'
                    )
                ]);

                if (cancelled) return;

                if (usersRes.status === 403) {
                    setTeamUsers([]);
                } else if (usersRes.ok) {
                    const usersData = await usersRes.json();
                    setTeamUsers(usersData.payload || []);
                } else {
                    toast.error('Failed to load task report data');
                    return;
                }

                if (tasksRes.status === 403) {
                    setReportTasksForbidden(true);
                    setAllTasks([]);
                } else if (tasksRes.ok) {
                    setReportTasksForbidden(false);
                    const tasksData = await tasksRes.json();
                    setAllTasks(tasksData.payload || []);
                } else {
                    setReportTasksForbidden(false);
                    toast.error('Failed to load task report data');
                }
            } catch (error) {
                if (cancelled) return;
                console.error('Error loading task report data:', error);
                toast.error('Error loading task report');
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        fetchReportData();
        return () => {
            cancelled = true;
        };
    }, [selectedQuarter, user]);

    const flattenedUsers = useMemo(() => {
        const rows = (teamUsers || []).flatMap((group) =>
            (group.users || []).map((u) => normalizeUser(u, group.team))
        );
        return dedupeReportUsersByEmail(rows);
    }, [teamUsers]);

    const visibleUsers = useMemo(() => {
        if (!user) return [];

        if (isLeadership) return flattenedUsers;

        if (isTeamLead) {
            return flattenedUsers.filter((u) => u.team === user?.hdTeam);
        }

        const me = flattenedUsers.find(
            (u) => u.email === (user.email || '').toLowerCase()
        );
        if (me) return [me];

        return [
            {
                email: (user.email || '').toLowerCase(),
                name: user.email || 'Current User',
                team: user?.hdTeam || 'Unassigned'
            }
        ];
    }, [flattenedUsers, isLeadership, isTeamLead, user]);

    const reportRows = useMemo(() => {
        const userTaskCounts = new Map();
        allTasks
            .filter((t) => !t.parent_task_id && t.item_type !== 'rock')
            .forEach((t) => {
                const email = (t.assignee_email || '').toLowerCase();
                if (!email) return;

                const existing = userTaskCounts.get(email) || {
                    completed: 0,
                    total: 0
                };
                existing.total += 1;
                if (t.status === 'completed') {
                    existing.completed += 1;
                }
                userTaskCounts.set(email, existing);
            });

        return [...visibleUsers].map((u) => {
            const counts = userTaskCounts.get(u.email) || {
                completed: 0,
                total: 0
            };
            return {
                ...u,
                completed: counts.completed,
                total: counts.total,
                completionRate: getCompletionRate(
                    counts.completed,
                    counts.total
                )
            };
        });
    }, [allTasks, visibleUsers]);

    const filteredAndSortedRows = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        const rowsAfterZeroFilter = hideZeroTasks
            ? reportRows.filter((row) => (Number(row.total) || 0) > 0)
            : reportRows;
        let rows = [...rowsAfterZeroFilter];

        if (query) {
            rows = rows.filter((row) =>
                `${row.name} ${row.email} ${row.team}`
                    .toLowerCase()
                    .includes(query)
            );
        }

        rows.sort((a, b) => {
            const direction = sortDirection === 'asc' ? 1 : -1;
            let comparison = 0;

            if (sortBy === 'name') {
                comparison = (a.name || a.email).localeCompare(
                    b.name || b.email
                );
            } else if (sortBy === 'team') {
                comparison = a.team.localeCompare(b.team);
            } else if (sortBy === 'completed') {
                comparison = a.completed - b.completed;
            } else if (sortBy === 'total') {
                comparison = a.total - b.total;
            } else if (sortBy === 'completionRate') {
                comparison = a.completionRate - b.completionRate;
            }

            return comparison * direction;
        });

        return rows;
    }, [hideZeroTasks, reportRows, searchTerm, sortBy, sortDirection]);

    const setSorting = (column) => {
        if (sortBy === column) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setSortBy(column);
        setSortDirection(
            column === 'name' || column === 'team' ? 'asc' : 'desc'
        );
    };

    const getSortLabel = (column) => {
        if (sortBy !== column) return '';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    const handleRowClick = (row) => {
        const params = new URLSearchParams();
        params.set('view', 'user');
        params.set('email', row.email);
        params.set('name', row.name || row.email);
        params.set('quarter', selectedQuarter);
        navigate(`/tasks?${params.toString()}`);
    };

    const handleExportCsv = () => {
        const headers = [
            'Quarter',
            'User',
            'Email',
            'Team',
            'CompletedTasks',
            'TotalTasks',
            'CompletionRate'
        ];

        const csvRows = filteredAndSortedRows.map((row) =>
            [
                selectedQuarter,
                row.name || row.email,
                row.email,
                row.team,
                row.completed,
                row.total,
                `${row.completionRate}%`
            ]
                .map(escapeCsvValue)
                .join(',')
        );

        const csvContent = [headers.join(','), ...csvRows].join('\n');
        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `task-report-${selectedQuarter}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (!user || isLoading) {
        return (
            <div className="loading-container">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="team-tasks-layout">
            <div className="global-team-tasks-header">
                <div className="header-left">
                    <h2>Task Report</h2>
                    <select
                        className="quarter-select"
                        value={selectedQuarter}
                        onChange={(e) => setSelectedQuarter(e.target.value)}>
                        {availableQuarters.map((q) => (
                            <option key={q} value={q}>
                                {q}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="header-right">
                    <Link
                        to={`/tasks?quarter=${selectedQuarter}`}
                        className="btn btn-secondary">
                        <FaArrowLeft className="btn-icon" />
                        Back to Tasks
                    </Link>
                </div>
            </div>

            <div className="team-tasks-body report-body">
                <div className="main-content-area report-main-content">
                    <div className="view-title">
                        <h1>{selectedQuarter} Task Report</h1>
                    </div>
                    {reportTasksForbidden && (
                        <p
                            style={{
                                margin: '0 0 1rem',
                                padding: '0 0.25rem',
                                color: '#6b7280',
                                fontSize: '0.95rem',
                                maxWidth: '40rem',
                                lineHeight: 1.5
                            }}>
                            Task totals aren’t available for your account. You
                            can still browse users in your scope; counts may be
                            empty.
                        </p>
                    )}
                    <div className="content-scroll-container report-content-scroll">
                        <div className="report-controls">
                            <div className="report-controls-left">
                                <input
                                    type="text"
                                    className="report-search-input"
                                    placeholder="Search by user, email, or team"
                                    value={searchTerm}
                                    onChange={(e) =>
                                        setSearchTerm(e.target.value)
                                    }
                                />
                                <label className="report-toggle-label">
                                    <input
                                        type="checkbox"
                                        checked={hideZeroTasks}
                                        onChange={(e) =>
                                            setHideZeroTasks(e.target.checked)
                                        }
                                    />
                                    Hide zero-task users
                                </label>
                                <span className="report-results-count">
                                    Showing {filteredAndSortedRows.length} of{' '}
                                    {reportRows.length} users
                                </span>
                            </div>
                            <div className="report-controls-right">
                                <button
                                    className="btn btn-export-report"
                                    onClick={handleExportCsv}
                                    disabled={
                                        filteredAndSortedRows.length === 0
                                    }>
                                    Export CSV
                                </button>
                            </div>
                        </div>
                        <div className="tasks-table-container report-table-container">
                            <div className="report-table-scroll">
                                <table className="tasks-table">
                                    <thead>
                                        <tr>
                                            <th>
                                                <button
                                                    className="report-sort-btn"
                                                    onClick={() =>
                                                        setSorting('name')
                                                    }>
                                                    User {getSortLabel('name')}
                                                </button>
                                            </th>
                                            <th>
                                                <button
                                                    className="report-sort-btn"
                                                    onClick={() =>
                                                        setSorting('team')
                                                    }>
                                                    Team {getSortLabel('team')}
                                                </button>
                                            </th>
                                            <th>
                                                <button
                                                    className="report-sort-btn"
                                                    onClick={() =>
                                                        setSorting('completed')
                                                    }>
                                                    Completed Tasks{' '}
                                                    {getSortLabel('completed')}
                                                </button>
                                            </th>
                                            <th>
                                                <button
                                                    className="report-sort-btn"
                                                    onClick={() =>
                                                        setSorting('total')
                                                    }>
                                                    Total Tasks{' '}
                                                    {getSortLabel('total')}
                                                </button>
                                            </th>
                                            <th>
                                                <button
                                                    className="report-sort-btn"
                                                    onClick={() =>
                                                        setSorting(
                                                            'completionRate'
                                                        )
                                                    }>
                                                    Completion Rate{' '}
                                                    {getSortLabel(
                                                        'completionRate'
                                                    )}
                                                </button>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAndSortedRows.map((row) => (
                                            <tr
                                                key={row.email}
                                                className="report-clickable-row"
                                                onClick={() =>
                                                    handleRowClick(row)
                                                }>
                                                <td>{row.name || row.email}</td>
                                                <td>{row.team}</td>
                                                <td>{row.completed}</td>
                                                <td>{row.total}</td>
                                                <td>{row.completionRate}%</td>
                                            </tr>
                                        ))}
                                        {filteredAndSortedRows.length === 0 && (
                                            <tr>
                                                <td
                                                    colSpan="5"
                                                    style={{
                                                        textAlign: 'center',
                                                        padding: '2rem'
                                                    }}>
                                                    No matching report data
                                                    found in {selectedQuarter}.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskReportPage;
